import { useState } from 'react'
import { useRequest } from 'ahooks'

import { Title } from 'components'
import { Table } from '@sar/component'

import { Spin, Checkbox, Button, Alert } from 'antd'
import { ModalForm, ProFormText } from '@ant-design/pro-components'

async function fetchData(store) {
  const result = await store.externals.api.get('/role/list')

  return {
    success: true,
    data: result,
  }
}

function Create ({ store, visible, handles, onVisibleChange }) {
  return <ModalForm
    title='新建角色'
    layout='vertical'
    grid={true}
    width={500}
    open={visible}
    initialValues={{ level: 3 }}
    modalProps={{
      maskClosable: false,
      destroyOnClose: true,
    }}
    onOpenChange={onVisibleChange}
    onFinish={params => store.externals.api.post('/role', params).then(() => handles.current.reload()).then(R.T)}
  >
    <ProFormText
      name='name'
      label='角色名称'
      rules={[ { required: true, message: '请输入角色名称' } ]}
    />
  </ModalForm>
}

function Update ({ data, store, visible, handles, onVisibleChange }) {
  return <ModalForm
    title='修改角色'
    layout='vertical'
    grid={true}
    width={500}
    open={visible}
    modalProps={{ maskClosable: false, destroyOnClose: true }}
    onOpenChange={onVisibleChange}
    request={async () => data}
    onFinish={params => store.externals.api.put(`/role/${data.id}`, params).then(() => handles.current.reload()).then(R.T)}
  >
    <ProFormText
      name='name'
      label='角色名称'
      rules={[ { required: true, message: '请输入角色名称' } ]}
    />
  </ModalForm>
}

function List ({ store, navigator }) {
  const columns = [
    {
      title: '名称',
      dataIndex: 'name',
      hideInSearch: true,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      valueType: 'datetime',
      hideInSearch: true,
    },
  ]

  const actions = [
    {
      name: '新增',
      type: 'trigger',
      form: props => <Create {...props} store={store} />,
      visible: () => store.identity.hasPermission('/system/roles:create'),
    },
  ]

  const rowActions = [
    {
      name: '修改',
      form: props => <Update {...props} store={store} />,
      visible: () => store.identity.hasPermission('/system/roles:update'),
    },
    {
      name: '权限',
      visible: () => store.identity.hasPermission('/system/roles:update'),
      onClick: data => navigator.to(`/system/roles/setting/${data.id}`, `设置角色权限（${data.name}）`),
    },
    {
      danger: true,
      name: '删除',
      confirmType: 'modal',
      confirmMessage: '您确定删除这个角色吗？',
      visible: () => store.identity.hasPermission('/system/roles:delete'),
      onConfirm: (data, handles) => store.externals.api.delete(`/role/${data.id}`).then(() => handles.current.reload()),
    },
  ]

  return <div className='p-4'>
    <Table
      title='角色列表'
      columns={columns}
      actions={actions}
      rowActions={rowActions}
      search={false}
      pagination={false}
      request={() => fetchData(store)}
    />
  </div>
}

function Setting ({ store, params, navigator }) {
  const [ selected, dispatch ] = useState({})

  const res = useRequest(async () => {
    const result = await store.externals.api.get(`/menu/all`).then(res => res.map(R.pick([
        'id'
      , 'pid'
      , 'title'
      , 'type'
      , 'children'
      , 'permission'
    ])))

    await store.externals.api.get(`/role/${params.id}/menu`).then(data => dispatch(
      R.fromPairs(data.map(item => [ item, true ]))
    ))

    let tree = {}
    let book = {}

    result.forEach(data => {
      Object.assign(book, { [ data.id ] : Object.assign(data, { children: [] }) })
    })

    result.forEach(data => {
      if (data.pid == null) {
        Object.assign(tree, { [ data.id ] : data })
      }

      else {
        Object.assign(book[data.pid], { children: book[data.pid].children.concat(data) })
      }
    })

    return Object.values<any>(tree).map(data => {
      function getChildren (data) {
        return [
            data
          , data.children.map(getChildren)
        ]
      }

      return Object.assign(data, {
        children: R.flatten(data.children.map(getChildren)).map(R.omit([ 'children' ]))
      })
    })
  }, {
    refreshDeps: [ params.id ]
  })

  const submit = useRequest(() => store.externals.api.put(`/role/${params.id}/permission`, R.toPairs<any>(selected).filter(([ _, item ]) => item).map(([ id ]) => ({ id }))), {
    manual: true
  })

  const total = React.useMemo(() => {
    if (res.data == null) {
      return -1
    }

    return R.compose(
      R.length,
      R.flatten<any>,
    )(
      res.data.map(data => [ data, data.children ])
    )
  }, [
    res.data
  ])

  const selectedAll = React.useMemo(() => {
    let data = Object.values(selected).filter(R.identity)

    if (data.length == total) {
      return true
    }

    else {
      return false
    }
  }, [
    Object.keys(selected)
  ])

  const getAllPermission = data => {
    return R.fromPairs(
      R.flatten<any>(data.map(data => [ data, data.children ])).map(data => [ data.id, true ])
    )
  }

  return <div className='m-4 p-4 bg-white min-h-[100%] space-y-6'>
    <div className='flex items-center space-x-2'>
      <Button type='default' onClick={() => navigator.back()}>返回</Button>
      <Button type='primary' onClick={() => submit.runAsync().then(() => navigator.back())} loading={submit.loading}>保存</Button>
    </div>

    <div>
      <Alert
        showIcon
        type='warning'
        description='修改角色权限后，当前登录账号拥有的权限不会立即发生改变。修改权限后请注销并重新登录，即可应用最新的权限配置。'
      />
    </div>

    <div className='pl-3'>
      <Checkbox checked={selectedAll} indeterminate={selectedAll ? false : R.isEmpty(R.filter(R.equals(true), selected)) == false} onChange={e => e.target.checked ? dispatch(getAllPermission(res.data)) : dispatch({})}>
        选中全部权限
      </Checkbox>
    </div>

    {res.loading ? (
      <div className='h-80 flex items-center justify-center'>
        <Spin/>
      </div>
    ) : res.data.map(data => {
      return <table key={data.id} className='border-collapse border border-slate-600 rounded w-full'>
        <thead>
          <tr>
            <th className='border border-slate-400 bg-gray-200 text-left p-3' colSpan={4}>
              <Checkbox checked={selected[data.id]} onChange={e => dispatch({ ...selected, [data.id]: e.target.checked })}>
                {data.title}
              </Checkbox>
            </th>
          </tr>
        </thead>

        <tbody>
          {data.children.filter(({ pid }) => pid == data.id).map(group => {
            return <>
              <tr>
                <td className='border border-slate-400 bg-gray-100 text-left p-3' colSpan={4}>
                  <Checkbox checked={selected[group.id]} onChange={e => dispatch({ ...selected, [group.id]: e.target.checked })}>
                    {group.title}
                  </Checkbox>
                </td>
              </tr>

              {R.splitEvery<any>(4, data.children.filter(({ pid }) => pid == group.id)).map(elms => {
                return <tr>
                  {R.times(index => elms?.[index], 4).map(elm => {
                    return <td className='border border-slate-400 text-left p-3 w-1/4'>
                      {elm == null ? null : (
                        <Checkbox checked={selected[elm.id]} onChange={e => dispatch({ ...selected, [elm.id]: e.target.checked })}>
                          {elm.title}
                        </Checkbox>
                      )}
                    </td>
                  })}
                </tr>
              })}
            </>
          })}
        </tbody>
      </table>
    })}
  </div>
}

export default function(prefix, app) {
  return app
    .useRoute(`${prefix}/setting/:id`, Setting)
    .useRoute(`${prefix}/`, List)
}
